import React, { useState, useEffect } from 'react';
import './FlowConsensusReal.css';
import './Consensus.css';

const FlowConsensusReal = ({ consensusMetrics = {}, networkStats = {}, shardData = [] }) => {
  const [consensusData, setConsensusData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch real consensus data from RPC
  useEffect(() => {
    const fetchConsensusData = async () => {
      setLoading(true);
      
      try {
        const response = await fetch('http://localhost:9545', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'avo_getNetworkStats',
            params: [],
            id: 1
          })
        });
        
        const result = await response.json();
        if (result.result) {
          setConsensusData(result.result);
        }
      } catch (err) {
        console.log('Using fallback consensus data:', err.message);
        setConsensusData(networkStats);
      } finally {
        setLoading(false);
      }
    };

    fetchConsensusData();
    const interval = setInterval(fetchConsensusData, 5000); // Update every 5s
    return () => clearInterval(interval);
  }, [networkStats]);

  const formatEfficiency = (efficiency) => {
    return `${(efficiency * 100).toFixed(2)}%`;
  };

  const getEfficiencyStatus = (efficiency) => {
    if (efficiency >= 0.98) return 'status-ok';
    if (efficiency >= 0.95) return 'status-warning';
    return 'status-error';
  };

  const metrics = consensusData || consensusMetrics;

  if (loading && !consensusData) {
    return (
      <div className="flow-consensus-real">
        <div className="consensus-loading">
          <div className="loading-spinner"></div>
          <p>Loading Flow Consensus Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flow-consensus-real">
      <div className="consensus-header-real">
        <h3>Flow Consensus Engine</h3>
        <div className="consensus-status-badge">
          <span className="status-dot status-ok"></span>
          <span>Active</span>
        </div>
      </div>

      {/* Main Metrics Grid */}
      <div className="consensus-metrics-grid-real">
        <div className="metric-card-real highlight">
          <div className="metric-icon">🏆</div>
          <div className="metric-content">
            <div className="metric-label">Current Epoch</div>
            <div className="metric-value">{metrics.current_epoch || 0}</div>
            <div className="metric-sublabel">Flow Consensus</div>
          </div>
        </div>

        <div className="metric-card-real">
          <div className="metric-icon">⚡</div>
          <div className="metric-content">
            <div className="metric-label">Consensus Efficiency</div>
            <div className={`metric-value ${getEfficiencyStatus(metrics.consensus_efficiency || 0)}`}>
              {formatEfficiency(metrics.consensus_efficiency || 0)}
            </div>
            <div className="metric-sublabel">
              {metrics.consensus_efficiency >= 0.98 ? 'Optimal' : 
               metrics.consensus_efficiency >= 0.95 ? 'Good' : 'Degraded'}
            </div>
          </div>
        </div>

        <div className="metric-card-real">
          <div className="metric-icon">⏱️</div>
          <div className="metric-content">
            <div className="metric-label">Block Time</div>
            <div className="metric-value">{(metrics.avg_block_time_ms || 0).toFixed(0)}ms</div>
            <div className="metric-sublabel">Average</div>
          </div>
        </div>

        <div className="metric-card-real">
          <div className="metric-icon">👥</div>
          <div className="metric-content">
            <div className="metric-label">Active Validators</div>
            <div className="metric-value">{metrics.active_validators || 0}</div>
            <div className="metric-sublabel">Securing network</div>
          </div>
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="consensus-details-real">
        <div className="detail-section-real">
          <h4>Network Performance</h4>
          <div className="detail-grid-real">
            <div className="detail-item-real">
              <span className="detail-label-real">TPS:</span>
              <span className="detail-value-real status-ok">
                {(metrics.avg_tps || 0).toFixed(2)} tx/s
              </span>
            </div>
            <div className="detail-item-real">
              <span className="detail-label-real">Latest Block:</span>
              <span className="detail-value-real">#{metrics.latest_block || 0}</span>
            </div>
            <div className="detail-item-real">
              <span className="detail-label-real">Connected Peers:</span>
              <span className="detail-value-real">{metrics.connected_peers || 0}</span>
            </div>
            <div className="detail-item-real">
              <span className="detail-label-real">Total Shards:</span>
              <span className="detail-value-real">{metrics.total_shards || 4}</span>
            </div>
          </div>
        </div>

        <div className="detail-section-real">
          <h4>Consensus Algorithm</h4>
          <div className="detail-grid-real">
            <div className="detail-item-real">
              <span className="detail-label-real">Type:</span>
              <span className="detail-value-real status-ok">Flow Consensus</span>
            </div>
            <div className="detail-item-real">
              <span className="detail-label-real">Leader Selection:</span>
              <span className="detail-value-real status-ok">VRF (sr25519)</span>
            </div>
            <div className="detail-item-real">
              <span className="detail-label-real">Multi-Shard:</span>
              <span className="detail-value-real status-ok">Enabled</span>
            </div>
            <div className="detail-item-real">
              <span className="detail-label-real">Cross-Shard:</span>
              <span className="detail-value-real status-ok">Supported</span>
            </div>
          </div>
        </div>
      </div>

      {/* Shard Status */}
      {shardData && shardData.length > 0 && (
        <div className="consensus-shards-real">
          <h4>Shard Status</h4>
          <div className="shard-grid-real">
            {shardData.map((shard) => (
              <div key={shard.id} className="shard-card-real">
                <div className="shard-header-real">
                  <span className="shard-name">Shard {shard.id}</span>
                  <span className={`shard-status-badge ${
                    shard.status === 'active' ? 'status-ok' : 'status-warning'
                  }`}>
                    {shard.status}
                  </span>
                </div>
                <div className="shard-metrics-real">
                  <div className="shard-metric-item">
                    <span className="shard-metric-label">Validators:</span>
                    <span className="shard-metric-value">{shard.validators || 0}</span>
                  </div>
                  <div className="shard-metric-item">
                    <span className="shard-metric-label">Blocks:</span>
                    <span className="shard-metric-value">{shard.blocks || 0}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Technical Details */}
      <div className="consensus-technical-real">
        <h4>Technical Implementation</h4>
        <div className="tech-grid-real">
          <div className="tech-item-real">
            <div className="tech-icon">🔐</div>
            <div className="tech-content">
              <div className="tech-title">VRF Integration</div>
              <div className="tech-description">Schnorrkel sr25519 for leader selection</div>
            </div>
          </div>
          <div className="tech-item-real">
            <div className="tech-icon">🌐</div>
            <div className="tech-content">
              <div className="tech-title">TCP Networking</div>
              <div className="tech-description">Real socket2 implementation</div>
            </div>
          </div>
          <div className="tech-item-real">
            <div className="tech-icon">🔄</div>
            <div className="tech-content">
              <div className="tech-title">State Sync</div>
              <div className="tech-description">RocksDB persistence</div>
            </div>
          </div>
          <div className="tech-item-real">
            <div className="tech-icon">⛓️</div>
            <div className="tech-content">
              <div className="tech-title">L1 Checkpoints</div>
              <div className="tech-description">Ethereum integration via ethers-rs</div>
            </div>
          </div>
        </div>
      </div>

      <div className="consensus-footer-real">
        <span>Last Updated: {new Date().toLocaleTimeString()}</span>
        <span>|</span>
        <span>All Data Real-Time</span>
      </div>
    </div>
  );
};

export default FlowConsensusReal;
